import { Instagram, Youtube, Play, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import post1 from "@/assets/proof-worker.jpg";
import post2 from "@/assets/proof-equipment.jpg";
import post3 from "@/assets/proof-plant.jpg";
import post4 from "@/assets/service-manpower.jpg";
import vid1 from "@/assets/brochure-industrial-cover.jpg";
import vid2 from "@/assets/brochure-safety-leadership.jpg";
import vid3 from "@/assets/brochure-utility-services.jpg";

const INSTAGRAM_URL = "https://www.instagram.com/arabianalwahaksa";
const YOUTUBE_URL = "https://youtube.com/@arabianalwaha-ksa";

const SocialFeed = () => {
  const { language } = useLanguage();
  const isAr = language === "ar";

  const posts = [
    { img: post1, caption: isAr ? "أطقم ماهرة في الموقع" : "Skilled crews on site" },
    { img: post2, caption: isAr ? "معدات جاهزة للنشر" : "Equipment ready to deploy" },
    { img: post3, caption: isAr ? "دعم مصانع تشغيلية" : "Live plant support" },
    { img: post4, caption: isAr ? "تعبئة القوى العاملة" : "Manpower mobilisation" },
  ];

  const videos = [
    { img: vid1, title: isAr ? "نظرة على الشركة" : "Company overview" },
    { img: vid2, title: isAr ? "السلامة أولاً في الموقع" : "Safety first on site" },
    { img: vid3, title: isAr ? "خدمات المرافق والصيانة" : "Utility & maintenance services" },
  ];

  return (
    <section className="bg-secondary/20 py-14 lg:py-20">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="font-montserrat text-3xl lg:text-4xl font-bold uppercase text-accent mb-3">
            {isAr ? "من مواقعنا الاجتماعية" : "Latest From Our Channels"}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {isAr ? "آخر المنشورات والفيديوهات من إنستغرام ويوتيوب." : "Recent posts and videos from Instagram and YouTube."}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Instagram */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Instagram className="w-5 h-5 text-primary" />
              <h3 className="font-bold uppercase tracking-wide text-foreground">Instagram</h3>
              <span className="text-sm text-muted-foreground">@arabianalwahaksa</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 gap-3">
              {posts.map((p) => (
                <a
                  key={p.caption}
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block aspect-square rounded-lg overflow-hidden ring-1 ring-border"
                >
                  <img src={p.img} alt={p.caption} loading="lazy" width={512} height={512} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute inset-0 bg-gradient-to-t from-primary-darker/80 to-transparent" />
                  <span className="absolute bottom-2 left-2 right-2 text-xs font-semibold text-primary-foreground">{p.caption}</span>
                </a>
              ))}
            </div>
            <Button asChild variant="outline" className="mt-4 border-2 border-accent text-accent hover:bg-accent hover:text-primary-darker">
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                {isAr ? "تابعنا على إنستغرام" : "Follow on Instagram"}
              </a>
            </Button>
          </div>

          {/* YouTube */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Youtube className="w-5 h-5 text-primary" />
              <h3 className="font-bold uppercase tracking-wide text-foreground">YouTube</h3>
              <span className="text-sm text-muted-foreground">@arabianalwaha-ksa</span>
            </div>
            <div className="space-y-3">
              {videos.map((v) => (
                <a
                  key={v.title}
                  href={YOUTUBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 rounded-lg bg-card ring-1 ring-border p-3 hover:shadow-elegant transition-shadow"
                >
                  <span className="relative w-32 shrink-0 aspect-video rounded-md overflow-hidden">
                    <img src={v.img} alt={v.title} loading="lazy" width={320} height={180} className="w-full h-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center bg-primary-darker/40">
                      <Play className="w-6 h-6 text-accent fill-current" />
                    </span>
                  </span>
                  <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{v.title}</span>
                </a>
              ))}
            </div>
            <Button asChild variant="outline" className="mt-4 border-2 border-accent text-accent hover:bg-accent hover:text-primary-darker">
              <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                {isAr ? "اشترك في قناتنا" : "Subscribe on YouTube"}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialFeed;